import { ProductsSearchEndpointParameters } from '@aboutyou/backbone';
import { Pagination } from '@aboutyou/backbone/endpoints/products/productsByIds';
import { PRICE_PROMOTION_KEY } from 'commerce/scayle/constants';
import { Product } from 'commerce/scayle/utils/adapter';
import { errorWrapper } from 'commerce/utils/network';
import { client } from '../client';

type ProductQueryParameters = Parameters<typeof client.products.query>;
type IPagination = Partial<Pagination>;

const productQuery = async (...args: ProductQueryParameters) => {
  const [params, ...rest] = args;
  const campaignKey = await getActiveCampaignKey();

  return client.products.query(
    {
      ...params,
      pricePromotionKey: PRICE_PROMOTION_KEY,
      includeSellableForFree: true,
      ...(campaignKey ? { campaignKey } : {}),
    },
    ...rest,
  );
};

const getCategoryById = (id: number) => {
  return errorWrapper(getCategoryById.name, () =>
    client.categories.getById(id),
  );
};

const productListWith: ProductsSearchEndpointParameters['with'] = {
  attributes: 'all',
  advancedAttributes: 'all',
  variants: {
    attributes: 'all',
    advancedAttributes: 'all',
  },
  images: 'all',
  siblings: {
    attributes: 'all',
    advancedAttributes: {
      withKey: ['masterProperties.headline', 'seo.url'],
    },
    variants: {
      attributes: 'all',
      advancedAttributes: {
        withKey: ['masterProperties.headline'],
      },
    },
  },
  categories: 'all',
  priceRange: true,
  baseCategories: true,
};

const getCategoryProducts = async (
  categoryId: number,
  pagination: IPagination,
) => {
  return errorWrapper('getCategoryProducts', async () => {
    const response = await productQuery({
      where: { categoryId },
      with: productListWith,
      pagination: {
        perPage: pagination?.perPage || 60,
        page: pagination?.page || 1,
      },
    });

    return {
      entities: response.entities.map((p) => Product.createProduct(p).toJSON()),
      pagination: response?.pagination,
    };
  });
};

export const getProductsByReferenceKeys = async (referenceKeys: string[]) => {
  return errorWrapper(getProductsByReferenceKeys.name, async () => {
    const { entities } = await productQuery({
      where: {
        attributes: [
          {
            type: 'attributes',
            key: 'variants.referenceKey',
            values: referenceKeys,
          },
        ],
      },
      with: productListWith,
    });

    return entities.map((p) => Product.createProduct(p).toShortJSON());
  });
};

export const getProductsByParentReferenceKeys = async (
  referenceKeys: string[],
) => {
  return errorWrapper(getProductsByParentReferenceKeys.name, async () => {
    const { entities } = await productQuery({
      where: {
        attributes: [
          {
            type: 'attributes',
            key: 'referenceKey',
            values: referenceKeys,
          },
        ],
      },
      with: productListWith,
    });

    return entities.map((p) => Product.createProduct(p).toJSON());
  });
};

/**
 * Get Product from scayle by Reference Key
 *
 * exm. satisfyer-satisfyer-pro-2-generation-3-16-5-cm-051888sf
 * @param {string} referenceKey
 * @param {boolean} byVariant
 */
export const getProductByKey = async (
  referenceKey: string,
  byVariant: boolean = false,
) => {
  return errorWrapper(getProductByKey.name, async () => {
    const { entities } = await productQuery({
      where: {
        attributes: [
          {
            type: 'attributes',
            key: byVariant ? 'variants.referenceKey' : 'referenceKey',
            values: [referenceKey],
          },
        ],
      },
      with: {
        attributes: 'all',
        advancedAttributes: 'all',
        variants: {
          attributes: 'all',
          advancedAttributes: 'all',
        },
        images: 'all',
        siblings: {
          advancedAttributes: {
            withKey: ['contents.contentValue', 'seo.url'],
          },
          attributes: {
            withKey: [
              'name',
              'productProperties.color',
              'contents.contentMeasuringUnit',
            ],
          },
        },
        categories: 'all',
        priceRange: true,
        baseCategories: true,
      },
    });

    return entities[0];
  });
};

export const getProductsByMasterKeys = async (masterKeys: string[]) => {
  return errorWrapper(getProductsByMasterKeys.name, async () => {
    const { entities } = await productQuery({
      where: {
        attributes: [
          {
            type: 'attributes',
            key: 'masterKey',
            values: masterKeys,
          },
        ],
      },
      with: {
        attributes: {
          withKey: ['contents.contentMeasuringUnit'],
        },
        advancedAttributes: {
          withKey: ['contents.contentValue', 'seo.url'],
        },
      },
    });

    return entities;
  });
};

export const getProductsByCategoryId = async ({
  id,
  pagination,
}: {
  id?: string;
  pagination: IPagination;
}) => {
  if (id) {
    const category = await getCategoryById(Number(id));

    if (!category.error) {
      return await getCategoryProducts(
        Number(category?.response?.id),
        pagination,
      );
    }
  }

  throw new Error("category doesn't exist");
};

export const getActiveCampaignKey = async () => {
  const { entities: campaigns } = await client.campaigns.get();

  const activeCampaigns = campaigns.filter(
    (campaign) => new Date(campaign.end_at as string) > new Date(),
  );

  return activeCampaigns?.[0]?.key;
};
